import { cn } from '@realtyfeed/utils';
import { clsx } from 'clsx';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { ChevronDown, User } from 'react-feather';
import type { ShellUser } from '../../type';
import { FOCUS_RING } from '../focusRing';

export type UserChipProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  user: ShellUser;
  /**
   * Active group's logo, drawn behind a vertical rule as in the switcher comp.
   * Omit it (or pass a group with no logo) for the comp's narrower chip.
   */
  logoUrl?: string | null;
  /** `chip` is the navbar trigger; `header` is the wider card atop the open panel. */
  variant?: 'chip' | 'header';
  /** Rotates the caret and sets `aria-expanded`. */
  open?: boolean;
};

/**
 * Avatar + name + email + caret: the navbar's menu trigger, and the header of
 * the account panel, as the same lockup at two widths.
 *
 * ### Why the chip has no border
 *
 * The navbar chip used to be a full card — a white `rounded-xl` box with a
 * `gdx-line` hairline, holding the caret inside a 2px-ringed indigo disc. Two
 * bordered boxes then sat side by side (the chip, and the menu it opened), and
 * the disc read as a second button inside the first. The comp draws none of
 * that: the navbar lockup is the avatar and the two lines of text directly on
 * the navbar, with a quiet caret for the affordance. So `chip` is now
 * borderless with a tinted hover — lifted off the navbar by the design system’s
 * resting `shadow-elevation-1` rather than by a hairline — and `header` — which
 * genuinely is a card inside the open panel — keeps the frame.
 *
 * `forwardRef` + prop spread so it can be dropped straight into a Radix
 * `DropdownMenuTrigger asChild`.
 */
export const UserChip = forwardRef<HTMLButtonElement, UserChipProps>(
  ({ user, logoUrl, variant = 'chip', open, className, ...props }, ref) => {
    const isHeader = variant === 'header';
    const name = displayName(user.name);

    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        aria-haspopup={open === undefined ? undefined : 'menu'}
        className={cn(
          'flex items-center text-start transition-colors',
          FOCUS_RING,
          isHeader
            ? 'w-full gap-3 rounded-xl border border-gdx-line bg-white p-3 hover:border-gdx-line-strong dark:border-slate-800 dark:bg-slate-900'
            : 'gap-3 rounded-xl px-2 py-1.5 shadow-elevation-1 hover:bg-ds-action-secondary-bg dark:shadow-slate-950/40 dark:hover:bg-slate-800',
          className
        )}
        {...props}
      >
        <Face user={user} size={isHeader ? 'lg' : 'sm'} />

        <span className="flex min-w-0 flex-1 flex-col justify-center">
          {name && (
            <span
              className={clsx(
                'truncate font-bold text-gdx-heading dark:text-white',
                isHeader ? 'text-base' : 'text-sm leading-tight'
              )}
            >
              {name}
            </span>
          )}
          {user.email && (
            <span
              className={clsx(
                'truncate text-gdx-nav-muted',
                isHeader ? 'text-sm' : 'text-xs leading-tight'
              )}
            >
              {user.email}
            </span>
          )}
        </span>

        {logoUrl && (
          <>
            <span
              className={clsx('w-px shrink-0 bg-gdx-line-strong', isHeader ? 'h-10' : 'h-7')}
              aria-hidden
            />
            <img
              src={logoUrl}
              alt=""
              className={clsx(
                'shrink-0 object-contain',
                isHeader ? 'max-h-8 max-w-[110px]' : 'max-h-7 max-w-[80px]'
              )}
            />
          </>
        )}

        <span
          className={clsx(
            'shrink-0 text-gdx-nav-muted transition-transform dark:text-slate-400',
            open && 'rotate-180'
          )}
          aria-hidden
        >
          <ChevronDown size={isHeader ? 18 : 16} strokeWidth={2.5} />
        </span>
      </button>
    );
  }
);

UserChip.displayName = 'UserChip';

/**
 * A name worth drawing, or `''`.
 *
 * A profile with no first/last name used to reach the chip as an em dash
 * placeholder, which rendered literally as the name *and* as the letter inside
 * the avatar disc. Requiring a letter catches that class of placeholder here,
 * once, rather than trusting every caller to pass the right kind of empty —
 * `\p{L}` so non-Latin names still count.
 */
function displayName(name: string | undefined): string {
  const trimmed = name?.trim() ?? '';
  return /\p{L}/u.test(trimmed) ? trimmed : '';
}

/** Profile picture, or the name's initial on a tinted disc when there isn't one. */
function Face({ user, size }: { user: ShellUser; size: 'sm' | 'lg' }) {
  const box = size === 'lg' ? 'h-12 w-12' : 'h-8 w-8';
  const initial = displayName(user.name).charAt(0).toUpperCase();

  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        className={clsx(box, 'shrink-0 rounded-full object-cover')}
      />
    );
  }

  return (
    <span
      className={clsx(
        box,
        'flex shrink-0 items-center justify-center rounded-full bg-gdx-line-strong font-bold text-ds-text-accent'
      )}
    >
      {initial || <User size={size === 'lg' ? 20 : 16} aria-hidden />}
    </span>
  );
}
